import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Statistic, Table, Tag, Typography, Select, Spin, message, Grid, Button, Space } from 'antd'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, UserOutlined, ApiOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { dashboardAPI, DashboardSummary, ProfilingRequest } from '../services/api'

const { Title } = Typography
const { Option } = Select
const { useBreakpoint } = Grid

const Dashboard: React.FC = () => {
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [recentRequests, setRecentRequests] = useState<ProfilingRequest[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState(30)
  const [refreshing, setRefreshing] = useState(false)

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [summaryData, requestsData] = await Promise.all([
        dashboardAPI.getSummary(selectedPeriod),
        dashboardAPI.getRecentRequests(10)
      ])
      
      setSummary(summaryData)
      setRecentRequests(requestsData.requests)
    } catch (error) {
      message.error('Error al cargar los datos del dashboard')
      console.error('Dashboard load error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboardData()
  }, [selectedPeriod])

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadDashboardData()
    setRefreshing(false)
    message.success('Dashboard actualizado')
  }

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'REJECTED':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      default:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'success'
      case 'REJECTED':
        return 'error'
      default:
        return 'warning'
    }
  }

  const decisionData = summary ? Object.entries(summary.decisions).map(([key, value]) => ({
    name: key,
    value: value
  })) : []

  const COLORS = ['#52c41a', '#ff4d4f', '#faad14', '#1890ff']

  const columns = [
    {
      title: 'ID Simulación',
      dataIndex: 'simulation_id',
      key: 'simulation_id',
      width: 120,
    },
    {
      title: 'Estado',
      dataIndex: 'decision_status',
      key: 'decision_status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Tipo Cliente',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 100,
    },
    {
      title: 'Score Crediticio',
      dataIndex: 'credit_score',
      key: 'credit_score',
      width: 120,
      render: (score: number) => score || 'N/A',
    },
    {
      title: 'Confianza IA',
      dataIndex: 'ai_confidence',
      key: 'ai_confidence',
      width: 100,
      render: (confidence: number) => confidence ? `${(confidence * 100).toFixed(1)}%` : 'N/A',
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <Title level={2} style={{ margin: 0 }}>Dashboard CreditFlow AI</Title>
        <Space wrap>
          <Button
            icon={<PlayCircleOutlined />}
            type="primary"
            onClick={() => navigate('/api-operations')}
          >
            {isMobile ? 'Testing' : 'Operaciones API'}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Actualizar
          </Button>
          <Select
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            style={{ width: isMobile ? 120 : 180 }}
          >
            <Option value={7}>Últimos 7 días</Option>
            <Option value={30}>Últimos 30 días</Option>
            <Option value={90}>Últimos 90 días</Option>
          </Select>
        </Space>
      </div>

      {/* Métricas principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Solicitudes"
              value={summary?.summary.total_requests || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Aprobadas"
              value={summary?.decisions.APPROVED || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Rechazadas"
              value={summary?.decisions.REJECTED || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tiempo Promedio"
              value={summary?.summary.avg_processing_time_seconds || 0}
              suffix="s"
              precision={1}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card title="Tendencia Diaria de Solicitudes">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary?.daily_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString('es-ES')}
                />
                <Bar dataKey="requests" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Distribución de Decisiones">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={decisionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {decisionData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Solicitudes recientes */}
      <Card title="Solicitudes Recientes">
        <Table
          columns={columns}
          dataSource={recentRequests}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: isMobile ? 980 : 800 }}
        />
      </Card>
    </div>
  )
}

export default Dashboard